import type { ApiClient } from "@vigia/client";
import { toSyncError } from "@vigia/powersync";
import { BasePowerSyncConnector, type CrudOperation, type TokenStorage } from "./base-connector";
import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from "../constants/common";

/** Total attempts (1 initial + retries) for a TRANSIENT token fetch failure. */
const DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS = 3;
/** Base backoff between transient retries; doubled each attempt. */
const DEFAULT_TOKEN_FETCH_RETRY_MS = 500;
/** Server errors at/above this are transient (retryable), unlike 4xx. */
const HTTP_SERVER_ERROR_MIN = 500;

export interface VigiaConnectorConfig {
  /** PowerSync Instance URL */
  endpoint: string;
  /** Shared token storage implementation */
  storage: TokenStorage;
  /** Pre-configured OpenAPI client */
  apiClient: ApiClient;
  /** Callback when auth errors (401/403) occur to notify session machines */
  onAuthError?: (error: unknown) => void;
  /** Optional guard to skip network calls if we know we are visitors */
  shouldFetchToken?: () => boolean;
  /**
   * Namespaces the cached JWT keys per account (multi-account). When omitted,
   * the legacy single-account keys are used.
   */
  cacheNamespace?: string;
  /**
   * Multi-account upload gate (CC-42): return `false` while this connector's
   * account is NOT the tab's active API identity so its queued CRUD is deferred
   * instead of being uploaded (and silently dropped/misattributed) under the
   * OTHER account's credentials. See `ConnectorConfig.canUploadNow`.
   */
  canUploadNow?: () => boolean;
  /**
   * Total attempts for a TRANSIENT `get-session` failure (network error / 5xx)
   * before giving up and returning `null`. A real 401/403 is NEVER retried.
   * Defaults to {@link DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS}. Exposed for tests.
   */
  maxTokenFetchAttempts?: number;
  /**
   * Base backoff (ms) between transient retries; doubled each attempt. Exposed
   * for tests (set to `0` to retry without real delay).
   */
  tokenFetchRetryBaseMs?: number;
}

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Shared PowerSync Connector for the Vigía ecosystem.
 * Consolidates common auth and batch upload logic used by both Web and Native apps.
 */
export class VigiaPowerSyncConnector extends BasePowerSyncConnector {
  constructor(vigiaConfig: VigiaConnectorConfig) {
    super({
      endpoint: vigiaConfig.endpoint,
      storage: vigiaConfig.storage,
      cacheNamespace: vigiaConfig.cacheNamespace,
      canUploadNow: vigiaConfig.canUploadNow,
      fetchAuthToken: async () => {
        if (vigiaConfig.shouldFetchToken && !vigiaConfig.shouldFetchToken()) {
          return null;
        }

        const maxAttempts = vigiaConfig.maxTokenFetchAttempts ?? DEFAULT_MAX_TOKEN_FETCH_ATTEMPTS;
        const retryBaseMs = vigiaConfig.tokenFetchRetryBaseMs ?? DEFAULT_TOKEN_FETCH_RETRY_MS;

        // Bounded retry/backoff so a TRANSIENT `get-session` failure (a network
        // blip or a 5xx) does NOT immediately surrender a `null` token — which
        // would leave PowerSync without credentials and the sync stuck
        // "Checking…". We DISTINGUISH error classes:
        //   • 401/403  → the session is genuinely invalid: notify the session
        //                machine and return `null` WITHOUT retrying.
        //   • network / 5xx → transient: retry with exponential backoff.
        // After exhausting the budget we return `null` (the caller still has its
        // valid-cache fallback) — never an infinite loop.
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            console.log("[PowerSync] Fetching auth token...");
            const { data, error, response } = await vigiaConfig.apiClient.GET(
              "/api/auth/get-session",
              {
                params: { query: { jwt: true } },
              },
            );

            const { ok, status } = response;

            if (ok && data) {
              return data.token ?? null;
            }

            if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
              console.warn("[PowerSync] Auth failed (401/403).");
              // Ensure the status is included so the session machine can recognize the auth failure
              const authError =
                error && typeof error === "object"
                  ? { ...error, status }
                  : { message: "Unauthorized", status };
              vigiaConfig.onAuthError?.(authError);
              return null;
            }

            // A non-auth, non-5xx, non-OK response (e.g. a 4xx other than
            // 401/403, or an OK response missing `data`) is not something a
            // retry will fix — give up immediately.
            if (status < HTTP_SERVER_ERROR_MIN) {
              console.warn(`[PowerSync] Token fetch failed (status ${status}); not retrying.`);
              return null;
            }

            console.warn(
              `[PowerSync] Token fetch transient failure (status ${status}); attempt ${attempt + 1}/${maxAttempts}.`,
            );
          } catch (err) {
            // A thrown error is a network failure (fetch rejected): transient.
            console.warn(
              `[PowerSync] Token fetch network error; attempt ${attempt + 1}/${maxAttempts}.`,
              err,
            );
          }

          const isLastAttempt = attempt === maxAttempts - 1;
          if (!isLastAttempt) {
            // oxlint-disable-next-line no-await-in-loop -- sequential backoff is intentional
            await sleep(retryBaseMs * 2 ** attempt);
          }
        }

        console.error("[PowerSync] Token fetch exhausted retries; returning null.");
        return null;
      },
      uploadBatch: async (operations: CrudOperation[]) => {
        const result = await vigiaConfig.apiClient.POST("/api/sync/batch", {
          body: { operations },
        });

        if (result.error) {
          // The HTTP STATUS travels with the error up to `classifyError`. Serializing
          // the body to text threw it away, and the classifier ended up guessing
          // by substrings: a `404` inside the prose of a 503 (a
          // `Retry-After: 3404`, an incident id) was read as a conflict and
          // DISCARDED the user's queued writes.
          throw toSyncError(result.error, result.response.status);
        }
      },
    });
  }
}
